import { apiClient } from "./client";
import type {
  MembershipKindInOrg,
  MembershipStatus,
  Page,
  Practitioner,
  PractitionerMembership,
  PractitionerStatus,
} from "./types";

export type ListPractitionersParams = {
  q?: string;
  status?: PractitionerStatus;
  limit?: number;
  offset?: number;
};

export async function listPractitioners(
  params: ListPractitionersParams = {},
): Promise<Page<Practitioner>> {
  const { data } = await apiClient.get<Page<Practitioner>>("/api/v1/practitioners", { params });
  return data;
}

export async function getPractitioner(id: string): Promise<Practitioner> {
  const { data } = await apiClient.get<Practitioner>(`/api/v1/practitioners/${id}`);
  return data;
}

export type CreatePractitionerPayload = {
  last_name: string;
  first_name?: string | null;
  first_initial?: string | null;
  birth_date?: string | null;
  rpps?: string | null;
  email?: string | null;
  phone?: string | null;
};

export async function createPractitioner(p: CreatePractitionerPayload): Promise<Practitioner> {
  const { data } = await apiClient.post<Practitioner>("/api/v1/practitioners", p);
  return data;
}

export type UpdatePractitionerPayload = Partial<CreatePractitionerPayload> & {
  status?: PractitionerStatus;
  annual_leave_days?: number;
};

export async function updatePractitioner(
  id: string,
  p: UpdatePractitionerPayload,
): Promise<Practitioner> {
  const { data } = await apiClient.patch<Practitioner>(`/api/v1/practitioners/${id}`, p);
  return data;
}

// ----------------------------------------------------------------------------
// Memberships
// ----------------------------------------------------------------------------
export async function listMemberships(
  practitionerId: string,
): Promise<PractitionerMembership[]> {
  const { data } = await apiClient.get<PractitionerMembership[]>(
    `/api/v1/practitioners/${practitionerId}/memberships`,
  );
  return data;
}

export type CreateMembershipPayload = {
  organization_id: string;
  kind: MembershipKindInOrg;
  status?: MembershipStatus;
  joined_at?: string | null;
  fte_percent?: number;
};

export async function createMembership(
  practitionerId: string,
  p: CreateMembershipPayload,
): Promise<PractitionerMembership> {
  const { data } = await apiClient.post<PractitionerMembership>(
    `/api/v1/practitioners/${practitionerId}/memberships`,
    p,
  );
  return data;
}

export async function updateMembership(
  practitionerId: string,
  membershipId: string,
  p: Partial<CreateMembershipPayload> & { left_at?: string | null },
): Promise<PractitionerMembership> {
  const { data } = await apiClient.patch<PractitionerMembership>(
    `/api/v1/practitioners/${practitionerId}/memberships/${membershipId}`,
    p,
  );
  return data;
}

export async function deleteMembership(
  practitionerId: string,
  membershipId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/practitioners/${practitionerId}/memberships/${membershipId}`,
  );
}

// ----------------------------------------------------------------------------
// Bulk CSV import
// ----------------------------------------------------------------------------
export type ImportRowError = {
  line: number;
  field: string | null;
  detail: string;
  row: Record<string, string> | null;
};

export type ImportReport = {
  dry_run: boolean;
  rows_processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: ImportRowError[];
};

export async function importPractitionersCsv(
  file: File,
  options: { dry_run?: boolean; update_existing?: boolean } = {},
): Promise<ImportReport> {
  const form = new FormData();
  form.append("file", file);
  const params: Record<string, string> = {};
  if (options.dry_run !== undefined) params.dry_run = String(options.dry_run);
  if (options.update_existing !== undefined)
    params.update_existing = String(options.update_existing);
  const { data } = await apiClient.post<ImportReport>(
    "/api/v1/practitioners/import",
    form,
    {
      params,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

// ----------------------------------------------------------------------------
// Bulk operations
// ----------------------------------------------------------------------------
export type BulkResult = {
  requested: number;
  processed: number;
  skipped: number;
  skipped_ids: string[];
};

export async function bulkArchivePractitioners(ids: string[]): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>(
    "/api/v1/practitioners/bulk-archive",
    { practitioner_ids: ids },
  );
  return data;
}

export type BulkAddMembershipPayload = {
  practitioner_ids: string[];
  organization_id: string;
  kind?: MembershipKindInOrg;
  status?: MembershipStatus;
};

export async function bulkAddMembership(p: BulkAddMembershipPayload): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>(
    "/api/v1/practitioners/bulk-add-membership",
    p,
  );
  return data;
}


export async function downloadBulkCvs(status?: "ACTIVE" | "INACTIVE"): Promise<Blob> {
  const { data } = await apiClient.get<Blob>("/api/v1/admin/practitioners/cvs.zip", {
    params: status ? { status } : {},
    responseType: "blob",
    timeout: 180_000,
  });
  return data;
}
